import { useEffect, useMemo, useRef, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { RenderItem } from "@/lib/render-items";
import {
  isSubAgentSpawnToolName,
  subAgentThreadReferenceFromResult,
  type SubAgentThreadReference,
} from "@/lib/sub-agent-thread";
import {
  detailSourceKey,
  isToolDetailsResponse,
  type HistoryToolDetailSource,
} from "@/lib/tool-details";

type ToolItem = Extract<RenderItem, { kind: "tool" }>;

function collectToolItems(items: readonly RenderItem[]): ToolItem[] {
  const tools: ToolItem[] = [];
  for (const item of items) {
    if (item.kind === "tool") tools.push(item);
    else if (item.kind === "tool-group") tools.push(...collectToolItems(item.items));
  }
  return tools;
}

function isSubAgentSpawn(tool: ToolItem): boolean {
  return isSubAgentSpawnToolName(tool.name);
}

function isTerminalSpawn(tool: ToolItem): boolean {
  return (
    (tool.status === "end" || tool.status === "error")
    && isSubAgentSpawn(tool)
  );
}

function isEarlyReferenceCandidate(tool: ToolItem): boolean {
  return tool.status === "start" && isSubAgentSpawn(tool);
}

/**
 * Resolve validated sub-agent references from live and persisted tool details.
 * A new server may put the exact V1 contract on a live tool-start after it
 * reserves the child session; terminal details are applied second and always
 * win. Missing, malformed, cross-parent, or legacy payloads deliberately
 * disappear from this map so older servers retain the ordinary pending card.
 */
export function useSubAgentReferences(
  sessionId: string,
  items: readonly RenderItem[],
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
): ReadonlyMap<string, SubAgentThreadReference> {
  const toolItems = useMemo(() => collectToolItems(items), [items]);
  const historySources = useMemo(() => {
    const sources = new Map<string, HistoryToolDetailSource>();
    for (const tool of toolItems) {
      if (!isTerminalSpawn(tool) || tool.source.kind !== "history") continue;
      sources.set(detailSourceKey(tool.source), tool.source);
    }
    return [...sources.values()];
  }, [toolItems]);
  const referencesBySourceRef = useRef(
    new Map<string, SubAgentThreadReference | null>(),
  );
  const controllersRef = useRef(new Map<string, AbortController>());
  const sessionRef = useRef(sessionId);
  const [revision, setRevision] = useState(0);
  const backendReconnectKey = useBackendReconnectKey();

  useEffect(() => {
    sessionRef.current = sessionId;
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current.clear();
    referencesBySourceRef.current.clear();
    setRevision((current) => current + 1);
    return () => {
      for (const controller of controllersRef.current.values()) controller.abort();
      controllersRef.current.clear();
    };
  }, [backendReconnectKey, sessionId]);

  useEffect(() => {
    for (const source of historySources) {
      if (source.sessionId !== sessionId) continue;
      const key = detailSourceKey(source);
      if (
        referencesBySourceRef.current.has(key)
        || controllersRef.current.has(key)
      ) continue;

      const controller = new AbortController();
      controllersRef.current.set(key, controller);
      void (async () => {
        let reference: SubAgentThreadReference | null = null;
        try {
          const response = await fetchWithAuth(
            `/api/sessions/${encodeURIComponent(source.sessionId)}/messages/${encodeURIComponent(source.messageId)}/tool-details`,
            { headers: {}, signal: controller.signal },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const payload: unknown = await response.json();
          if (
            isToolDetailsResponse(payload)
            && payload.message_id === source.messageId
            && isSubAgentSpawnToolName(payload.tool_name)
            && payload.result.state === "available"
          ) {
            reference = subAgentThreadReferenceFromResult(
              payload.result.value,
              sessionId,
              payload.parameters.state === "available"
                ? payload.parameters.value
                : undefined,
            );
            if (!reference) {
              console.warn("[sub-agent-thread] ignored invalid persisted spawn reference", {
                sessionId,
                messageId: source.messageId,
              });
            }
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.info("[sub-agent-thread] persisted spawn details unavailable", {
              sessionId,
              messageId: source.messageId,
              error: error instanceof Error ? error.name : "unknown",
            });
          }
        }

        if (
          controller.signal.aborted
          || sessionRef.current !== source.sessionId
          || controllersRef.current.get(key) !== controller
        ) return;
        controllersRef.current.delete(key);
        referencesBySourceRef.current.set(key, reference);
        setRevision((current) => current + 1);
      })();
    }
  }, [backendReconnectKey, fetchWithAuth, historySources, sessionId]);

  return useMemo(() => {
    const references = new Map<string, SubAgentThreadReference>();
    // Apply early start details first. The UI can open a child transcript
    // while the native tool call remains in progress.
    for (const tool of toolItems) {
      if (!isEarlyReferenceCandidate(tool) || tool.source.kind !== "live") continue;
      const { result, parameters } = tool.source.details;
      const reference = result.state === "available"
        ? subAgentThreadReferenceFromResult(
            result.value,
            sessionId,
            parameters.state === "available" ? parameters.value : undefined,
          )
        : null;
      if (reference) references.set(tool.id, reference);
    }
    // Terminal payloads are authoritative even when a start reference was
    // accepted earlier. This specifically regresses a scheduler failure from
    // clickable back to a grey, unavailable card.
    for (const tool of toolItems) {
      if (!isTerminalSpawn(tool)) continue;
      if (tool.source.kind === "live") {
        const { result, parameters } = tool.source.details;
        const reference = result.state === "available"
          ? subAgentThreadReferenceFromResult(
              result.value,
              sessionId,
              parameters.state === "available" ? parameters.value : undefined,
            )
          : null;
        if (reference) references.set(tool.id, reference);
      } else if (tool.source.kind === "history") {
        const reference = referencesBySourceRef.current.get(
          detailSourceKey(tool.source),
        );
        if (reference) references.set(tool.id, reference);
      }
    }
    return references;
  }, [revision, sessionId, toolItems]);
}
